<template>
  <div class="w-full min-h-screen flex flex-col">
    <MainNav :inverted="true" />
    
    <!-- Hero Section with Article Header -->
    <section class="w-full relative">
      <img 
        :src="article?.heroImage || '/image/83islands_location.jpg'" 
        :alt="article?.title"
        class="w-full h-[600px] object-cover object-center"
      />
      
      <!-- Text Overlay -->
      <div class="mx-auto absolute inset-0 z-20 w-full p-4 lg:p-36 flex flex-col items-center justify-center">
        <div class="max-w-4xl text-center">
          <div class="inline-block mb-4 px-4 py-2 bg-black/70 backdrop-blur-sm rounded-full">
            <span class="text-white text-sm font-maax uppercase tracking-wide">{{ article?.publication || '83 Islands Distillery' }}</span>
          </div>
          <h1 class="text-white font-maax text-3xl uppercase lg:text-5xl font-bold leading-tight drop-shadow-[0_5px_5px_rgba(0,0,0,0.5)] mb-4">
            {{ article?.title }}
          </h1>
          <div class="flex items-center justify-center gap-4 text-white/90 text-sm">
            <span v-if="article?.date">{{ formatDate(article.date) }}</span>
            <span v-if="article?.author">By {{ article.author }}</span>
          </div>
        </div>
      </div>

      <!-- Double Wave -->
      <div class="absolute -bottom-1 left-0 right-0 w-full overflow-hidden">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" class="w-full h-20 text-white">
          <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" 
                fill="currentColor" class="opacity-25"/>
          <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" 
                fill="currentColor"/>
        </svg>
      </div>
    </section>

    <!-- Article Content -->
    <main class="flex-grow w-full bg-white">
      <article class="max-w-4xl mx-auto px-4 py-12 lg:py-16">
        <!-- Article Meta -->
        <div class="mb-8 pb-8 border-b border-gray-200">
          <div class="flex flex-wrap items-center gap-4 text-sm text-gray-600">
            <span v-if="article?.date" class="flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              {{ formatDate(article.date) }}
            </span>
            <span v-if="article?.author" class="flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
              {{ article.author }}
            </span>
            <span v-if="article?.location" class="flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
              {{ article.location }}
            </span>
          </div>
        </div>

        <!-- Article Body -->
        <div class="prose prose-lg max-w-none font-maax">
          <!-- Introduction/Lead Paragraph -->
          <div v-if="article?.lead" class="mb-8">
            <p class="text-2xl font-maax text-black leading-relaxed italic border-l-4 border-gold pl-6 py-4">
              {{ article.lead }}
            </p>
          </div>

          <!-- Main Content Sections -->
          <div v-if="article?.content" class="space-y-8">
            <div 
              v-for="(section, index) in article.content" 
              :key="index"
              class="article-section"
            >
              <!-- Section Heading -->
              <h2 v-if="section.heading" class="text-3xl font-maax font-bold uppercase text-black mb-4 mt-12 first:mt-0">
                {{ section.heading }}
              </h2>

              <!-- Section Image (full width) -->
              <div v-if="section.image" class="my-8">
                <img 
                  :src="section.image.url" 
                  :alt="section.image.alt || section.heading"
                  class="w-full h-auto rounded-lg shadow-lg"
                  :class="section.image.fullWidth ? 'md:h-[500px] object-cover' : 'max-w-2xl mx-auto'"
                />
                <p v-if="section.image.caption" class="text-sm text-gray-500 italic mt-2 text-center">
                  {{ section.image.caption }}
                </p>
              </div>

              <!-- Section Image (side by side) -->
              <div v-if="section.images && section.images.length > 0" class="my-8">
                <div :class="[
                  'grid gap-6',
                  section.images.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'
                ]">
                  <div v-for="(img, imgIndex) in section.images" :key="imgIndex">
                    <img 
                      :src="img.url" 
                      :alt="img.alt || ''"
                      class="w-full h-[300px] object-cover rounded-lg shadow-lg"
                    />
                    <p v-if="img.caption" class="text-sm text-gray-500 italic mt-2">
                      {{ img.caption }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Section Paragraphs -->
              <div class="space-y-4 text-xl text-black leading-relaxed">
                <p v-for="(paragraph, pIndex) in section.paragraphs" :key="pIndex">
                  {{ paragraph }}
                </p>
              </div>

              <!-- Quote Block -->
              <div v-if="section.quote" class="my-8 p-6 bg-gray-50 border-l-4 border-gold rounded-r-lg">
                <p class="text-xl font-maax text-black italic mb-2">
                  "{{ section.quote.text }}"
                </p>
                <p v-if="section.quote.attribution" class="text-sm font-maax text-gray-600">
                  — {{ section.quote.attribution }}
                </p>
              </div>
            </div>
          </div>

          <!-- Back to News Link -->
          <div class="mt-12 pt-8 border-t border-gray-200">
            <router-link 
              to="/in-the-news" 
              class="inline-flex items-center gap-2 text-gold font-maax font-semibold hover:underline"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
              </svg>
              Back to In the News
            </router-link>
          </div>
        </div>
      </article>

      <!-- Related Articles or Call to Action -->
      <div class="bg-black text-white py-16">
        <div class="max-w-7xl mx-auto px-4 text-center">
          <h3 class="text-3xl font-maax font-bold mb-4">Experience the Taste of Vanuatu</h3>
          <p class="text-xl mb-8 max-w-2xl mx-auto">
            Visit 83 Islands Distillery in Port Vila and discover the authentic spirit of Vanuatu through our premium rums.
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <router-link 
              to="/tours" 
              class="font-maax px-8 py-3 uppercase font-bold transition-all rounded-lg border-2 text-white hover:bg-white hover:text-black hover:border-white text-center"
            >
              Book a Tour
            </router-link>
            <router-link 
              to="/contact" 
              class="font-maax px-8 py-3 uppercase font-bold transition-all rounded-lg border-2 text-white hover:bg-white hover:text-black hover:border-white text-center"
            >
              Contact Us
            </router-link>
          </div>
        </div>
      </div>
    </main>

    <Footer class="relative" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import MainNav from '../components/MainNav.vue'
import Footer from '../components/Footer.vue'
import { getArticleById } from '../data/articles.js'
import { useStructuredData } from '../composables/useStructuredData'

const route = useRoute()
const article = ref(null)
const { injectArticleStructuredData } = useStructuredData()

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })
}

onMounted(() => {
  const articleId = route.params.id
  if (articleId) {
    article.value = getArticleById(articleId)
    
    // Update page title
    if (article.value) {
      document.title = `${article.value.title} | 83 Islands Distillery`
      
      // Update meta description
      let descriptionMeta = document.querySelector('meta[name="description"]')
      if (!descriptionMeta) {
        descriptionMeta = document.createElement('meta')
        descriptionMeta.name = 'description'
        document.head.appendChild(descriptionMeta)
      }
      descriptionMeta.content = article.value.lead || article.value.title
      
      // Update or create Open Graph tags
      const baseUrl = 'https://83islands.com'
      const articleUrl = `${baseUrl}/article/${article.value.id}`
      const ogImage = `${baseUrl}${article.value.heroImage || '/image/83islands_location.jpg'}`
      
      const updateOrCreateMeta = (property, content) => {
        let meta = document.querySelector(`meta[property="${property}"]`)
        if (!meta) {
          meta = document.createElement('meta')
          meta.setAttribute('property', property)
          document.head.appendChild(meta)
        }
        meta.setAttribute('content', content)
      }
      
      // Open Graph tags
      updateOrCreateMeta('og:title', article.value.title)
      updateOrCreateMeta('og:description', article.value.lead || article.value.title)
      updateOrCreateMeta('og:image', ogImage)
      updateOrCreateMeta('og:url', articleUrl)
      updateOrCreateMeta('og:type', 'article')
      updateOrCreateMeta('og:site_name', '83 Islands Distillery')
      
      // Twitter Card tags
      updateOrCreateMeta('twitter:card', 'summary_large_image')
      updateOrCreateMeta('twitter:title', article.value.title)
      updateOrCreateMeta('twitter:description', article.value.lead || article.value.title)
      updateOrCreateMeta('twitter:image', ogImage)
      
      // Article-specific meta
      if (article.value.date) {
        updateOrCreateMeta('article:published_time', new Date(article.value.date).toISOString())
      }
      updateOrCreateMeta('article:author', article.value.author || '83 Islands Distillery')
      if (article.value.location) {
        updateOrCreateMeta('article:content_location', article.value.location)
      }
      
      // Canonical URL
      let canonical = document.querySelector('link[rel="canonical"]')
      if (!canonical) {
        canonical = document.createElement('link')
        canonical.setAttribute('rel', 'canonical')
        document.head.appendChild(canonical)
      }
      canonical.setAttribute('href', articleUrl)

      // Add alternate links for machine-readable formats (helps AI agents and crawlers)
      const addAlternateLink = (type, href, title) => {
        let link = document.querySelector(`link[rel="alternate"][type="${type}"]`)
        if (!link) {
          link = document.createElement('link')
          link.setAttribute('rel', 'alternate')
          link.setAttribute('type', type)
          document.head.appendChild(link)
        }
        link.setAttribute('href', href)
        link.setAttribute('title', title)
      }

      addAlternateLink('application/json', `${baseUrl}/api/article?id=${article.value.id}`, `${article.value.title} (JSON)`)
      addAlternateLink('text/html', `${baseUrl}/api/article?id=${article.value.id}&format=html`, `${article.value.title} (Static HTML)`)
      addAlternateLink('text/plain', `${baseUrl}/api/article?id=${article.value.id}&format=text`, `${article.value.title} (Plain Text)`)
      
      // Inject Article structured data for Google
      injectArticleStructuredData(article.value)
    }
  }
})

// Watch for article changes
watch(() => article.value, (newArticle) => {
  if (newArticle) {
    injectArticleStructuredData(newArticle)
  }
})
</script>

<style scoped>
.prose {
  color: inherit;
}

.prose p {
  margin-bottom: 1.5rem;
}

.text-gold {
  color: #F6D993;
}

.article-section {
  scroll-margin-top: 2rem;
}
</style>
